import { View, Text, StyleSheet } from "@react-pdf/renderer";
import type { PdfWidgetSnapshot } from "../pdfTypes";
import type { PdfRect } from "../pdfLayout";

// Renders the text widget natively in react-pdf by parsing the stored (resolved)
// HTML — no rasterization. All CSS px values are scaled to PDF points so the
// output matches the dashboard's .text-widget exactly; content taller than the
// widget is clipped by the inner overflow:hidden box (truncation).

const s = StyleSheet.create({
  cell: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "#e8e8e8",
    borderRadius: 4,
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  fallback: { fontSize: 9, color: "#333", lineHeight: 1.5, padding: 10 },
});

const BASE_PX = 14; // .text-widget-content base font-size
const PAD_X_PX = 16; // .text-widget horizontal padding
const PAD_TOP_PX = 6; // .text-widget top padding
const PAD_BOTTOM_PX = 12; // .text-widget bottom padding
const HEADING_PX: Record<string, number> = { h1: 28, h2: 22, h3: 18, h4: 15 };

function px(value: string): number | null {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

// Largest inline font-size (px) anywhere in a block, defaulting to `fallback`.
// react-pdf sizes a line box from the block's own font size, so a block with a
// big inline span would otherwise clip/overlap; we use this to grow the line box.
function maxFontSizePx(el: HTMLElement, fallback: number): number {
  let max = fallback;
  const own = px(el.style.fontSize);
  if (own != null) max = Math.max(max, own);
  el.querySelectorAll<HTMLElement>("[style]").forEach((d) => {
    const f = px(d.style.fontSize);
    if (f != null) max = Math.max(max, f);
  });
  return max;
}

let keyCounter = 0;

function inlineStyleFor(el: HTMLElement, scale: number) {
  const st: Record<string, string | number> = {};
  switch (el.tagName.toLowerCase()) {
    case "strong":
    case "b": st.fontWeight = 700; break;
    case "em":
    case "i": st.fontStyle = "italic"; break;
    case "u": st.textDecoration = "underline"; break;
    case "s":
    case "del":
    case "strike": st.textDecoration = "line-through"; break;
    case "a": st.color = "#1677ff"; break;
    case "mark": st.backgroundColor = el.style.backgroundColor || "#ffe58f"; break;
  }

  const color = el.style.color?.toLowerCase();
  if (color && !["inherit", "currentcolor", "initial", "unset"].includes(color)) {
    st.color = el.style.color;
  }
  if (el.style.backgroundColor) st.backgroundColor = el.style.backgroundColor;
  const fs = px(el.style.fontSize);
  if (fs != null) st.fontSize = fs * scale;
  return st;
}

function renderInline(node: ChildNode, scale: number): React.ReactNode {
  if (node.nodeType === 3) return node.textContent; // text node
  if (node.nodeType !== 1) return null;
  const el = node as HTMLElement;
  if (el.tagName.toLowerCase() === "br") return "\n";
  const children = Array.from(el.childNodes).map((c) => renderInline(c, scale));
  return (
    <Text key={keyCounter++} style={inlineStyleFor(el, scale)}>
      {children}
    </Text>
  );
}

function renderBlock(el: HTMLElement, scale: number): React.ReactNode {
  const tag = el.tagName.toLowerCase();
  const key = keyCounter++;

  if (tag === "hr") {
    return <View key={key} style={{ borderBottomWidth: 1, borderBottomColor: "#e8ecf0", marginTop: 10 * scale, marginBottom: 10 * scale }} />;
  }

  if (tag === "ul" || tag === "ol") {
    return (
      <View key={key} style={{ marginBottom: 8 * scale, paddingLeft: 20 * scale }}>
        {Array.from(el.children).map((li, i) => {
          const liFs = maxFontSizePx(li as HTMLElement, BASE_PX);
          return (
            <Text key={i} style={{ fontSize: liFs * scale, lineHeight: 1.3, marginBottom: 2 * scale }}>
              {tag === "ol" ? `${i + 1}. ` : "•  "}
              {Array.from(li.childNodes).map((c) => renderInline(c, scale))}
            </Text>
          );
        })}
      </View>
    );
  }

  if (tag === "blockquote") {
    return (
      <View key={key} style={{ borderLeftWidth: 3 * scale, borderLeftColor: "#d9d9d9", paddingLeft: 12 * scale, marginBottom: 8 * scale }}>
        {Array.from(el.children).length
          ? Array.from(el.children).map((c) => renderBlock(c as HTMLElement, scale))
          : (
            <Text style={{ fontSize: maxFontSizePx(el, BASE_PX) * scale, lineHeight: 1.3, color: "#666" }}>
              {Array.from(el.childNodes).map((c) => renderInline(c, scale))}
            </Text>
          )}
      </View>
    );
  }

  const headingPx = HEADING_PX[tag];
  const baseFs = headingPx ?? BASE_PX;
  // Use the block's dominant (largest) inline font size as the paragraph font
  // size so the parent and its spans agree — react-pdf then sizes the line box
  // correctly.
  const blockFs = maxFontSizePx(el, baseFs);
  const textAlign = (el.style.textAlign || "left") as "left" | "center" | "right" | "justify";
  // Honor an explicit inline line-height (set via the editor's Line spacing
  // control); react-pdf lineHeight is a unitless multiplier like CSS.
  const lineHeight = px(el.style.lineHeight) ?? (headingPx ? 1.15 : 1.3);
  return (
    <Text
      key={key}
      style={{
        fontSize: blockFs * scale,
        fontWeight: headingPx ? 700 : 400,
        lineHeight,
        textAlign,
        marginBottom: (headingPx ? 6 : 8) * scale,
      }}
    >
      {Array.from(el.childNodes).map((c) => renderInline(c, scale))}
    </Text>
  );
}

export function renderText(
  snapshot: Extract<PdfWidgetSnapshot, { type: "text" }>,
  rect: PdfRect,
  scale: number,
) {
  let blocks: React.ReactNode;
  try {
    const doc = new DOMParser().parseFromString(snapshot.content || "", "text/html");
    keyCounter = 0;
    blocks = Array.from(doc.body.children).map((el) => renderBlock(el as HTMLElement, scale));
  } catch {
    blocks = <Text style={s.fallback}>{snapshot.content}</Text>;
  }

  return (
    <View style={[s.cell, { left: rect.left, top: rect.top, width: rect.width, height: rect.height }]}>
      <View
        style={{
          position: "absolute",
          top: PAD_TOP_PX * scale,
          left: PAD_X_PX * scale,
          right: PAD_X_PX * scale,
          bottom: PAD_BOTTOM_PX * scale,
          overflow: "hidden",
        }}
      >
        {/* Position the content out of flow so it can't grow the clip box.
            react-pdf only clips overflow:hidden against an absolutely-positioned
            child; in normal flow the text paints past the box (no truncation).
            Top-anchored, so content taller than the box is clipped at the bottom
            — matching the dashboard's scrolled-to-top view. */}
        <View style={{ position: "absolute", top: 0, left: 0, right: 0 }}>
          {blocks}
        </View>
      </View>
    </View>
  );
}
